import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useRef, useState } from "react";
import { Button, Dialog, Modal, ModalOverlay } from "react-aria-components";
import { toast } from "sonner";
import { Lightbox } from "@/components/Lightbox";
import { useOpenTemplateEditor } from "@/modules/plus/data/components/EditTemplateDialog";
import { ModelNotes } from "@/modules/plus/data/components/ModelNotes";
import { validatePhotoFile } from "@/modules/plus/photos/plus.photos.api";
import type { Model } from "@/modules/plus/plus.schema";
import { atoms } from "@/state";
import { BasicPhotoGallery } from "./BasicPhotoGallery";
import { ModelDetail } from "./ModelDetail";

// Centered modal that renders model info as an overlay without navigating
// away. Mounted once in app/layout.tsx so the underlying page state (search
// input, filter results, scroll position) is preserved across opens.

const ModelPhotoHeader: React.FC<{
  modelCode: string;
  model: Model | null;
  description: string | null;
  series: string | null;
  onPhotoClick: (url: string) => void;
}> = ({ modelCode, model, description, series, onPhotoClick }) => {
  // Fall back to the accessory_photos gallery keyed by the model code. That's
  // where user-uploaded photos for a model without a canonical photo_url land.
  const galleryPhotos = useAtomValue(atoms.photos.byCode(modelCode));
  const uploadMutation = useAtomValue(atoms.photos.upload);
  const removeMutation = useAtomValue(atoms.photos.remove);
  const signedIn = useAtomValue(atoms.auth.isSignedIn);
  const fileInput = useRef<HTMLInputElement>(null);

  // Prefer the canonical photo_url; otherwise show the first gallery photo.
  // Track which one we're showing so deletion can target the right entry —
  // canonical (catalog) photos are read-only, gallery uploads are deletable.
  const galleryHead = galleryPhotos[0];
  const displayUrl = model?.photo_url || galleryHead?.url || null;
  const canDelete =
    signedIn &&
    !model?.photo_url &&
    galleryHead?.source === "uploaded" &&
    Boolean(galleryHead.imagePath);
  const busy = uploadMutation.isPending || removeMutation.isPending;

  const onRemove = async () => {
    if (!canDelete || !galleryHead?.imagePath) {
      return;
    }
    if (!window.confirm(`Delete this photo for ${modelCode}? This can't be undone.`)) {
      return;
    }
    try {
      await removeMutation.mutateAsync({
        code: modelCode,
        imagePath: galleryHead.imagePath,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Remove failed");
    }
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const validationError = validatePhotoFile(file);
    if (validationError) {
      toast.error(validationError);
      if (fileInput.current) {
        fileInput.current.value = "";
      }
      return;
    }
    try {
      await uploadMutation.mutateAsync({ code: modelCode, file });
      toast.success(`Photo added for ${modelCode}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      if (fileInput.current) {
        fileInput.current.value = "";
      }
    }
  };

  return (
    <header className="flex items-start gap-5">
      {displayUrl ? (
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => onPhotoClick(displayUrl)}
            aria-label="View full-size photo"
            className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-transform hover:scale-[1.02] dark:border-gray-700 dark:bg-gray-800"
          >
            <img
              src={displayUrl}
              alt={description ?? modelCode}
              className="h-40 w-40 object-contain"
            />
          </button>
          {canDelete && (
            <button
              type="button"
              disabled={busy}
              onClick={onRemove}
              title="Remove this photo"
              aria-label="Remove this photo"
              className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-white text-sm leading-none text-red-600 shadow ring-1 ring-red-300 hover:bg-red-50 disabled:opacity-50"
            >
              ×
            </button>
          )}
        </div>
      ) : signedIn ? (
        <button
          type="button"
          disabled={uploadMutation.isPending}
          onClick={() => fileInput.current?.click()}
          className="grid h-40 w-40 shrink-0 cursor-pointer place-items-center rounded-lg border-2 border-dashed border-gray-300 text-center text-xs text-gray-500 hover:border-blue-500 hover:text-blue-600 disabled:opacity-50 dark:border-gray-700 dark:text-gray-400 dark:hover:border-blue-400 dark:hover:text-blue-400"
        >
          <span>
            {uploadMutation.isPending ? (
              "Uploading…"
            ) : (
              <>
                + Upload photo
                <br />
                <span className="text-[10px] text-gray-400 dark:text-gray-500">≤ 5 MB</span>
              </>
            )}
          </span>
        </button>
      ) : (
        <div className="grid h-40 w-40 shrink-0 place-items-center rounded-lg border border-dashed border-gray-300 text-xs text-gray-400 dark:border-gray-700 dark:text-gray-500">
          No photo
        </div>
      )}
      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={onFile}
        className="hidden"
      />
      <div className="flex min-w-0 flex-col gap-1">
        <div className="font-mono text-2xl font-semibold dark:text-gray-100">{modelCode}</div>
        {description && <p className="text-sm text-gray-800 dark:text-gray-200">{description}</p>}
        {series && <p className="text-xs text-gray-500 dark:text-gray-400">{series}</p>}
        {model?.product_page && (
          <a
            href={model.product_page}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 self-start text-xs text-blue-600 hover:underline dark:text-blue-400"
          >
            Open on nailor.com →
          </a>
        )}
      </div>
    </header>
  );
};

export const ModelSheet: React.FC = () => {
  const selection = useAtomValue(atoms.models.sheetSelected);
  const close = useSetAtom(atoms.models.closeSheet);
  const [_, setSelection] = useAtom(atoms.models.sheetSelected);
  const signedIn = useAtomValue(atoms.auth.isSignedIn);
  const openTemplateEditor = useOpenTemplateEditor();
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const isOpen = selection !== null;

  // Description/series can come from either the model record or the template heading.
  const description = selection?.model?.description ?? null;
  const series = selection?.model?.series ?? selection?.template?.heading ?? null;

  return (
    <>
      <ModalOverlay
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSelection(null);
          }
        }}
        isDismissable
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6 backdrop-blur-[2px]"
      >
        <Modal className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-gray-50 shadow-2xl outline-none dark:bg-gray-950">
          <Dialog className="flex min-h-0 flex-1 flex-col outline-none">
            <header className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3 dark:border-gray-800 dark:bg-gray-900">
              <div className="font-mono text-sm font-semibold dark:text-gray-100">
                {selection?.modelCode ?? ""}
              </div>
              <div className="flex items-center gap-2">
                {signedIn && selection && (
                  <Button
                    onPress={() =>
                      openTemplateEditor({
                        modelCode: selection.modelCode,
                        initial: selection.template,
                      })
                    }
                    className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    {selection.template ? "Edit template" : "Build template"}
                  </Button>
                )}
                <Button
                  onPress={() => close()}
                  className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  Close
                </Button>
              </div>
            </header>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {selection && (
                <div className="flex flex-col gap-6">
                  <ModelPhotoHeader
                    modelCode={selection.modelCode}
                    model={selection.model}
                    description={description}
                    series={series}
                    onPhotoClick={setLightboxUrl}
                  />
                  {selection.template ? (
                    <ModelDetail template={selection.template} />
                  ) : (
                    <>
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
                        <div className="font-medium">No option-code breakdown yet</div>
                        <p className="mt-1 text-amber-800 dark:text-amber-300">
                          We have this model in the database, but the field-by-field meaning of its
                          full ordering key hasn't been extracted from the catalog yet.
                        </p>
                      </div>
                      <BasicPhotoGallery modelCode={selection.modelCode} />
                    </>
                  )}
                  <ModelNotes modelCode={selection.modelCode} />
                </div>
              )}
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>

      <Lightbox
        url={lightboxUrl}
        alt={description ?? selection?.modelCode}
        isOpen={lightboxUrl !== null}
        onOpenChange={(open) => {
          if (!open) {
            setLightboxUrl(null);
          }
        }}
      />
    </>
  );
};
